/** Plan state shared by the desktop and mobile Plan: server data + live drafts (screens.md §1 "Type in Assigned"). */
import { withAssigned, type PlanCategoryDto, type PlanDto, type PlanGroupDto } from '@koverta/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../components/toast';
import { ApiError, errorMessage, post, put } from '../lib/api';
import { useHousehold } from '../lib/household';
import { keys, queryClient, usePlan } from '../lib/queries';

export type Filter = 'all' | 'under' | 'over' | 'avail' | 'snz';
export const FILTERS: [Filter, string][] = [
  ['all', 'All'],
  ['under', 'Underfunded'],
  ['over', 'Overspent'],
  ['avail', 'Available'],
  ['snz', 'Snoozed'],
];

export function passFilter(c: PlanCategoryDto, f: Filter, month: string) {
  switch (f) {
    case 'all':
      return true;
    case 'under':
      return c.state === 'warn';
    case 'over':
      return c.state === 'neg';
    case 'avail':
      return c.available > 0;
    case 'snz':
      return !!c.target && c.target.snoozedMonth === month;
  }
}

export interface PlanModel {
  plan: PlanDto | undefined;
  groups: PlanGroupDto[];
  cats: PlanCategoryDto[];
  byId: Map<string, PlanCategoryDto>;
  rta: number;
  underfunded: number;
  isLoading: boolean;
  error: unknown;
  /** Live (keystroke) value; the server write is debounced. */
  setDraft: (id: string, amount: number) => void;
  /** Immediate write (sheet "Assign", auto-assign buttons). */
  assignNow: (id: string, amount: number) => Promise<void>;
  move: (from: string, to: string, amount: number) => Promise<void>;
  autoAssign: (strategy: string, categoryIds?: string[]) => Promise<void>;
  undo: () => Promise<void>;
}

export function usePlanModel(month: string): PlanModel {
  const { household } = useHousehold();
  const { toast } = useToast();
  const q = usePlan(month);
  const [drafts, setDrafts] = useState<Record<string, number>>({});
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const baseline = useRef<Record<string, number>>({});

  // Drop drafts once the server agrees with them.
  useEffect(() => {
    if (!q.data) return;
    setDrafts((d) => {
      const next = { ...d };
      let changed = false;
      for (const [id, v] of Object.entries(d)) {
        const server = q.data!.groups.flatMap((g) => g.categories).find((c) => c.id === id);
        if (server && server.assigned === v && !timers.current[id]) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : d;
    });
  }, [q.data]);

  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  const write = useCallback(
    async (id: string, amount: number) => {
      const expected = baseline.current[id];
      try {
        await put(`/api/plan/${month}/assign`, { categoryId: id, amount, expected });
        baseline.current[id] = amount;
      } catch (e) {
        setDrafts(({ [id]: _, ...rest }) => rest);
        delete baseline.current[id];
        if (e instanceof ApiError && e.code === 'assigned_changed') {
          toast('Your partner changed this amount meanwhile — refreshed');
        } else toast(errorMessage(e));
      }
      await queryClient.invalidateQueries({ queryKey: keys.plan(month) });
    },
    [month, toast],
  );

  const serverAssigned = useCallback(
    (id: string) => q.data?.groups.flatMap((g) => g.categories).find((c) => c.id === id)?.assigned ?? 0,
    [q.data],
  );

  const setDraft = useCallback(
    (id: string, amount: number) => {
      if (!(id in baseline.current)) baseline.current[id] = serverAssigned(id);
      setDrafts((d) => ({ ...d, [id]: amount }));
      clearTimeout(timers.current[id]);
      timers.current[id] = setTimeout(() => {
        delete timers.current[id];
        void write(id, amount);
      }, 400);
    },
    [serverAssigned, write],
  );

  const assignNow = useCallback(
    async (id: string, amount: number) => {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
      if (!(id in baseline.current)) baseline.current[id] = serverAssigned(id);
      setDrafts((d) => ({ ...d, [id]: amount }));
      await write(id, amount);
    },
    [serverAssigned, write],
  );

  const derived = useMemo(() => {
    if (!q.data)
      return { groups: [] as PlanGroupDto[], cats: [] as PlanCategoryDto[], rta: 0, underfunded: 0 };
    let delta = 0;
    let underfunded = 0;
    const groups = q.data.groups.map((g) => ({
      ...g,
      categories: g.categories.map((c) => {
        const d = drafts[c.id];
        if (d === undefined || d === c.assigned) {
          if (!c.hidden) underfunded += c.need;
          return c;
        }
        delta += d - c.assigned;
        const row = withAssigned(c, c.target, month, d, household.weekStart);
        if (!c.hidden) underfunded += row.need;
        return {
          ...c,
          ...row,
          auto: { ...c.auto, underfunded: d + row.need },
        };
      }),
    }));
    return { groups, cats: groups.flatMap((g) => g.categories), rta: q.data.rta - delta, underfunded };
  }, [q.data, drafts, month, household.weekStart]);

  const byId = useMemo(() => new Map(derived.cats.map((c) => [c.id, c])), [derived.cats]);

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn();
      } catch (e) {
        toast(errorMessage(e));
      }
      baseline.current = {};
      await queryClient.invalidateQueries({ queryKey: keys.plan(month) });
    },
    [month, toast],
  );

  return {
    plan: q.data,
    ...derived,
    byId,
    isLoading: q.isPending,
    error: q.error,
    setDraft,
    assignNow,
    move: (from, to, amount) => run(() => post(`/api/plan/${month}/move`, { from, to, amount })),
    autoAssign: (strategy, categoryIds) =>
      run(() => post(`/api/plan/${month}/auto-assign`, { strategy, categoryIds })),
    undo: () =>
      run(async () => {
        await post('/api/plan/undo');
        toast('Undone');
      }),
  };
}
